import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface CollapsedSidebarRailButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  /**
   * "This is the live, current context" marker (CH-15). Renders a small blue
   * underline-style bar beneath the icon — the same accent family as the
   * active-tab underline (CH-09 §6: structure is blue, never orange) — so the
   * rail's own vocabulary for "this is current" matches the rest of the app's.
   */
  isActive?: boolean;
}

/**
 * Shared shell for one 40px activity-rail entry (CH-15 — replaces the
 * collapsed sidebar's old vertical-text labels). An icon-only button with an
 * optional active-state marker; callers supply the accessible name via
 * `aria-label` and wrap this in `Tooltip`/`DropdownMenuTrigger` as needed —
 * both compose with `asChild`, so this component only has to forward its ref
 * and spread props onto a plain `<button>` (same shape as every other
 * `asChild` trigger in the app, e.g. `SchemaExportDropdown`'s
 * Tooltip-over-DropdownMenuTrigger button).
 */
export const CollapsedSidebarRailButton = forwardRef<HTMLButtonElement, CollapsedSidebarRailButtonProps>(
  ({ icon, isActive, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'relative flex h-8 w-8 items-center justify-center rounded hover:bg-muted transition-colors cursor-pointer',
        className
      )}
      {...props}
    >
      {icon}
      {isActive && (
        <span
          aria-hidden="true"
          data-testid="rail-active-marker"
          className="absolute bottom-0 left-1/2 h-[2px] w-4 -translate-x-1/2 rounded-full bg-primary"
        />
      )}
    </button>
  )
);
CollapsedSidebarRailButton.displayName = 'CollapsedSidebarRailButton';
